import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Zap, Clock, Flame, ArrowRight, Check, Shield,
  Sparkles, Trophy, Star, TrendingUp, Gift, Crown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePricing } from '../utils/PricingContext';
import { getDiscountTimeRemaining } from '../utils/pricing';

/**
 * Modal popup for upgrade prompts
 * Uses PricingContext - NO HARDCODED PRICES
 */
const UpgradeModal = ({
  isOpen,
  onClose,
  variant = 'standard',
  featureName = '',
  limitInfo = null,
}) => {
  const navigate = useNavigate();
  const { getStarterPlan, getUltimatePlan, formatPrice, loading } = usePricing();
  const [timeRemaining, setTimeRemaining] = React.useState({ hours: 23, minutes: 59, seconds: 59, expired: false });

  const starterPlan = getStarterPlan();
  const ultimatePlan = getUltimatePlan();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(getDiscountTimeRemaining());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleUpgrade = () => {
    onClose();
    navigate('/subscribe');
  };

  if (!isOpen) return null;

  // Show loading state if plans haven't loaded yet
  const starterPrice = starterPlan ? formatPrice(starterPlan.price, starterPlan.currency) : '...';
  const starterOriginal = starterPlan ? formatPrice(starterPlan.original_price, starterPlan.currency) : '...';
  const starterDuration = starterPlan?.duration_months || 1;

  const ultimatePrice = ultimatePlan ? formatPrice(ultimatePlan.price, ultimatePlan.currency) : '...';
  const ultimateOriginal = ultimatePlan ? formatPrice(ultimatePlan.original_price, ultimatePlan.currency) : '...';
  const ultimateDuration = ultimatePlan?.duration_months || 12;
  const ultimatePerMonth = ultimatePlan ? formatPrice(Math.round(ultimatePlan.price / ultimateDuration), ultimatePlan.currency) : '...';

  // Limit Reached Variant
  if (variant === 'limit-reached') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative bg-gradient-to-br from-[#0F131F] to-[#1a1f2e] border border-red-500/30 rounded-3xl p-8 max-w-lg w-full shadow-2xl"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-4">
                <TrendingUp className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white font-mono mb-2">LIMIT REACHED</h2>
              <p className="text-slate-400 font-mono text-sm">You've used all your {featureName || 'free'} credits</p>
            </div>

            {limitInfo && (
              <div className="bg-black/30 border border-white/10 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-400 font-mono text-sm">Usage</span>
                  <span className="text-white font-mono">{limitInfo.used}/{limitInfo.limit}</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2">
                  <div className="bg-red-500 h-2 rounded-full" style={{ width: '100%' }} />
                </div>
              </div>
            )}

            <div className="space-y-3 mb-6">
              <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-4 cursor-pointer hover:border-emerald-500/50 transition-colors" onClick={handleUpgrade}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Crown className="w-6 h-6 text-emerald-400" />
                    <div>
                      <p className="text-white font-bold font-mono">Ultimate (Best Value)</p>
                      <p className="text-emerald-400 text-sm">Unlimited {featureName} for {ultimateDuration} months</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500 text-xs line-through">{ultimateOriginal}</p>
                    <p className="text-white font-bold">{ultimatePrice}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4 cursor-pointer hover:border-white/20 transition-colors" onClick={handleUpgrade}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Zap className="w-6 h-6 text-blue-400" />
                    <div>
                      <p className="text-white font-bold font-mono">Starter</p>
                      <p className="text-slate-400 text-sm">{starterDuration} month access</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500 text-xs line-through">{starterOriginal}</p>
                    <p className="text-white font-bold">{starterPrice}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-2 text-orange-400 mb-4">
              <Clock className="w-4 h-4" />
              <span className="font-mono text-sm">
                Discount expires in: {String(timeRemaining.hours).padStart(2, '0')}:{String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
              </span>
            </div>

            <button
              onClick={handleUpgrade}
              className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl flex items-center justify-center space-x-2 hover:scale-105 transition-transform"
            >
              <Zap className="w-5 h-5" />
              <span className="font-mono">UPGRADE & UNLOCK</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Special Offer Variant
  if (variant === 'special-offer') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative bg-gradient-to-br from-[#0F131F] via-[#1a1f2e] to-[#0F131F] border border-emerald-500/30 rounded-3xl p-8 max-w-md w-full shadow-2xl overflow-hidden"
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-teal-500/10 rounded-full blur-3xl animate-pulse" />
            </div>

            <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors z-10">
              <X className="w-6 h-6" />
            </button>

            <div className="relative z-10">
              <div className="text-center mb-6">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-full mb-4"
                >
                  <Gift className="w-10 h-10 text-emerald-400" />
                </motion.div>
                <div className="inline-flex items-center space-x-2 bg-red-500/20 border border-red-500/30 px-4 py-2 rounded-full mb-4">
                  <Flame className="w-5 h-5 text-red-400 animate-pulse" />
                  <span className="text-red-400 font-mono font-bold text-sm">LIMITED TIME OFFER</span>
                  <Flame className="w-5 h-5 text-red-400 animate-pulse" />
                </div>
                <h2 className="text-3xl font-bold text-white font-mono mb-2">SPECIAL DEAL</h2>
                <p className="text-slate-400">Unlock your full career potential today</p>
              </div>

              <div className="bg-black/30 border border-emerald-500/30 rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <div className="text-center">
                    <p className="text-slate-500 text-lg line-through">{ultimateOriginal}</p>
                    <p className="text-4xl font-bold text-white">{ultimatePrice}</p>
                    <p className="text-emerald-400 text-sm">Only {ultimatePerMonth}/month for {ultimateDuration} months</p>
                  </div>
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-2 rounded-xl font-bold text-xl transform -rotate-12">
                    -50%
                  </div>
                </div>

                <div className="space-y-2">
                  {['Unlimited Career Roadmaps', 'AI Resume Optimizer', 'Interview Prep', 'Priority Support'].map((feature, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center space-x-4">
                  <Clock className="w-6 h-6 text-orange-400" />
                  <div className="flex space-x-2 font-mono text-white">
                    <div className="bg-black/50 px-3 py-2 rounded-lg">
                      <p className="text-2xl font-bold">{String(timeRemaining.hours).padStart(2, '0')}</p>
                      <p className="text-[10px] text-slate-500">HOURS</p>
                    </div>
                    <span className="text-orange-400 text-2xl">:</span>
                    <div className="bg-black/50 px-3 py-2 rounded-lg">
                      <p className="text-2xl font-bold">{String(timeRemaining.minutes).padStart(2, '0')}</p>
                      <p className="text-[10px] text-slate-500">MINS</p>
                    </div>
                    <span className="text-orange-400 text-2xl">:</span>
                    <div className="bg-black/50 px-3 py-2 rounded-lg">
                      <p className="text-2xl font-bold">{String(timeRemaining.seconds).padStart(2, '0')}</p>
                      <p className="text-[10px] text-slate-500">SECS</p>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleUpgrade}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl flex items-center justify-center space-x-2 hover:scale-105 transition-transform shadow-lg shadow-emerald-500/30"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-mono text-lg">CLAIM YOUR DISCOUNT</span>
              </button>

              <p className="text-center text-slate-500 text-xs mt-4 font-mono">
                <Shield className="w-3 h-3 inline mr-1" />
                14-day money-back guarantee
              </p>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Standard Variant
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative bg-gradient-to-br from-[#0F131F] to-[#1a1f2e] border border-white/10 rounded-3xl p-8 max-w-lg w-full shadow-2xl"
        >
          <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-full mb-4">
              <Trophy className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white font-mono mb-2">UNLOCK PREMIUM</h2>
            <p className="text-slate-400">Take your career to the next level</p>
          </div>

          <div className="space-y-3 mb-6">
            {[
              { icon: Star, text: 'Unlimited AI-powered career roadmaps', color: 'text-yellow-400' },
              { icon: TrendingUp, text: 'Advanced analytics & insights', color: 'text-blue-400' },
              { icon: Sparkles, text: 'AI resume optimizer & interview prep', color: 'text-purple-400' },
              { icon: Shield, text: 'Priority support & mentorship', color: 'text-emerald-400' },
            ].map((benefit, i) => (
              <div key={i} className="flex items-center space-x-3 bg-white/5 rounded-lg p-3">
                <benefit.icon className={`w-5 h-5 ${benefit.color}`} />
                <span className="text-slate-300 text-sm">{benefit.text}</span>
              </div>
            ))}
          </div>

          <div className="bg-black/30 border border-emerald-500/20 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Ultimate Plan ({ultimateDuration} months)</p>
                <div className="flex items-baseline space-x-2">
                  <span className="text-slate-500 line-through">{ultimateOriginal}</span>
                  <span className="text-3xl font-bold text-white">{ultimatePrice}</span>
                </div>
                <p className="text-emerald-400 text-xs mt-1">Only {ultimatePerMonth}/month</p>
              </div>
              <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full font-bold">
                Save 50%
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center space-x-2 text-orange-400 mb-6">
            <Clock className="w-4 h-4" />
            <span className="font-mono text-sm">
              Offer ends in: {String(timeRemaining.hours).padStart(2, '0')}:{String(timeRemaining.minutes).padStart(2, '0')}:{String(timeRemaining.seconds).padStart(2, '0')}
            </span>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleUpgrade}
              className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl flex items-center justify-center space-x-2 hover:scale-105 transition-transform"
            >
              <Zap className="w-5 h-5" />
              <span className="font-mono">UPGRADE NOW</span>
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 text-slate-500 hover:text-slate-300 font-mono text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UpgradeModal;
